import React, { useState, useRef, useEffect } from 'react';
import { signOut, onAuthStateChanged } from 'firebase/auth';
import { auth } from '../firebase';
import { getDatabase, ref, set, get } from 'firebase/database';
import { useNavigate } from 'react-router-dom';
import ResumeUploader from '../components/ResumeUploader';
import { Bar } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js';
import ReactMarkdown from 'react-markdown'; // Import for formatting the AI's response

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

// Helper component for the new prompt buttons to avoid repetition
const PromptButton = ({ text, onClick, disabled }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    style={{
      padding: '8px 16px',
      fontSize: '14px',
      border: `1px solid ${disabled ? '#ccc' : '#007bff'}`,
      borderRadius: '20px',
      backgroundColor: disabled ? '#f5f5f5' : 'white',
      color: disabled ? '#aaa' : '#007bff',
      cursor: disabled ? 'not-allowed' : 'pointer',
      margin: '0 5px',
      transition: 'background-color 0.2s, color 0.2s',
    }}
  >
    {text}
  </button>
);


function Chatbot() {
  const navigate = useNavigate();
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const chatContainerRef = useRef(null);
  
  const [resumeSummary, setResumeSummary] = useState('');
  const [resumeAnalysis, setResumeAnalysis] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);

  // Effect 1: Check for authenticated user
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
      setIsLoading(false);
    });
    return () => unsubscribe();
  }, []);

  // Effect 2: Fetch data when the user is confirmed
  useEffect(() => {
    if (!currentUser) {
      setResumeSummary('');
      setResumeAnalysis(null);
      return;
    }
    const dbRef = ref(getDatabase(), `resumeAnalysis/${currentUser.uid}`);
    get(dbRef).then((snapshot) => {
      if (snapshot.exists()) {
        const savedData = snapshot.val();
        setResumeSummary(savedData.summary);
        setResumeAnalysis(savedData.analysis);
      }
    }).catch((error) => {
      console.error("Error fetching saved data:", error);
    });
  }, [currentUser]);

  // Auto-scroll for chat messages
  useEffect(() => {
    if (chatContainerRef.current) {
      const { scrollHeight, clientHeight } = chatContainerRef.current;
      chatContainerRef.current.scrollTop = scrollHeight - clientHeight;
    }
  }, [messages]);

  // General message sending logic, now accepts an optional display message
  const sendMessage = async (messageContent, displayContent) => {
    if (!messageContent.trim()) return;

    // Use the displayContent for the UI, or default to the messageContent
    const userMessage = { role: 'user', content: displayContent || messageContent };
    
    setMessages(prevMessages => [...prevMessages, userMessage]);
    setLoading(true);

    let payloadMessages = [...messages, { role: 'user', content: messageContent }];

    if (resumeSummary) {
      const modifiedLastMessage = { ...payloadMessages[payloadMessages.length - 1] };
      modifiedLastMessage.content = `Based on the following resume summary, please answer the user's question. 
IMPORTANT: Only respond to questions related to careers, professional development, or education. If the question is off-topic, politely state that you can only assist with career-related queries.
---
RESUME SUMMARY: ${resumeSummary}
---
USER'S QUESTION: ${messageContent}`;
      
      payloadMessages[payloadMessages.length - 1] = modifiedLastMessage;
    }
    
    try {
      const res = await fetch('https://coursehub-282e6.uc.r.appspot.com/api/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ messages: payloadMessages }),
      });

      const data = await res.json();
      
      const assistantMessage = { 
        role: 'assistant', 
        content: (data && data.reply) ? data.reply : '❌ Empty or malformed reply from server.'
      };
      setMessages(prevMessages => [...prevMessages, assistantMessage]);
    } catch (err) {
      const errorMessage = { role: 'assistant', content: '❌ Error contacting GuideAI server.' };
      setMessages(prevMessages => [...prevMessages, errorMessage]);
    }

    setLoading(false);
  };

  // Handler for manual text input
  const handleManualSend = () => {
    sendMessage(input); // Here, the message and display content are the same
    setInput('');
  };

  // Handler for the predefined prompt buttons
  const sendPredefinedPrompt = (timeframe) => {
    // This is the full, detailed prompt that gets sent to the AI
    const fullPrompt = `Based on my resume, create a high-quality, 4-point action plan to guide me for the next ${timeframe}.`;
    // This is the short message that will appear in the chat UI
    const displayPrompt = `Guide me for the next ${timeframe}.`;
    sendMessage(fullPrompt, displayPrompt);
  };

  // Handle user logout
  const handleLogout = () => {
    signOut(auth).then(() => navigate('/'));
  };

  // Handle a new resume analysis
  const handleUploadComplete = (data) => {
    if (currentUser && data && data.summary && data.analysis) {
      setResumeSummary(data.summary);
      setResumeAnalysis(data.analysis);
      const dbRef = ref(getDatabase(), `resumeAnalysis/${currentUser.uid}`);
      set(dbRef, data);
    }
  };

  // Chart configuration
  const chartOptions = {
    responsive: true,
    plugins: {
      legend: { display: false },
      title: {
        display: true,
        text: 'Resume Skill Analysis',
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        max: 100,
        title: {
            display: true,
            text: 'Score (0-100)'
        }
      },
    },
  };

  const chartData = {
    labels: ['Leadership & People Mgmt', 'Technical Skills'],
    datasets: [
      {
        label: 'Score',
        data: resumeAnalysis ? [resumeAnalysis.leadership_people_mgmt, resumeAnalysis.technical_skills] : [],
        backgroundColor: ['rgba(54, 162, 235, 0.6)', 'rgba(255, 99, 132, 0.6)'],
        borderColor: ['rgba(54, 162, 235, 1)', 'rgba(255, 99, 132, 1)'],
        borderWidth: 1,
      },
    ],
  };

  return (
    <div style={{ fontFamily: 'Arial', padding: '2rem 4rem', backgroundColor: '#f5f5f5', minHeight: '100vh', overflow: 'auto' }}>
      {/* --- MODIFICATION START --- */}
      {/* I've wrapped the buttons in a div with flexbox for easy alignment and spacing. */}
      <div style={{ position: 'absolute', top: '20px', right: '20px', display: 'flex', gap: '10px' }}>
        
        {/* This is the new button for upgrading to a Pro account. */}
        <button 
          onClick={() => navigate('/upgrade')} // This will navigate to a new page.
          style={{ 
            padding: '10px 20px', 
            fontSize: '16px', 
            border: '2px solid #ffc107', 
            borderRadius: '6px', 
            backgroundColor: '#ffc107', // A gold/amber color to stand out.
            color: '#212529', // Dark text for contrast.
            fontWeight: 'bold',
            cursor: 'pointer' 
          }}
        >
          ✨ Upgrade to Pro
        </button>

        {/* The existing logout button remains the same. */}
        <button onClick={handleLogout} style={{ padding: '10px 20px', fontSize: '16px', border: '2px solid red', borderRadius: '6px', backgroundColor: 'white', cursor: 'pointer' }}>
          Logout
        </button>
      </div>
      {/* --- MODIFICATION END --- */}

      <h1 style={{ marginBottom: '20px' }}>🎓 GuideAI Chatbot</h1>

      <ResumeUploader onUploadComplete={handleUploadComplete} />

      {isLoading ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
          <p>Authenticating...</p>
        </div>
      ) : resumeAnalysis ? (
        <div style={{ maxWidth: '800px', margin: '30px auto', padding: '20px', border: '1px solid #ddd', borderRadius: '8px', backgroundColor: 'white' }}>
          <h2 style={{ borderBottom: '2px solid #eee', paddingBottom: '10px', textAlign: 'center' }}>📄 Resume Analysis</h2>
          <h3>Summary</h3>
          <p style={{ whiteSpace: 'pre-wrap', lineHeight: '1.6' }}>{resumeSummary}</p>
          
          <div style={{ marginTop: '20px' }}>
             <Bar options={chartOptions} data={chartData} />
          </div>
        </div>
      ) : (
        <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
          <p>No resume found in memory. Upload one to get started.</p>
        </div>
      )}

      {/* --- Predefined prompt buttons --- */}
      <div style={{ maxWidth: '800px', margin: '30px auto', padding: '20px 0', borderTop: '1px solid #eee', textAlign: 'center' }}>
        <h3 style={{color: '#555', marginBottom: '15px'}}>Need some guidance?</h3>
        <PromptButton text="Next 1 Month" onClick={() => sendPredefinedPrompt('1 month')} disabled={!resumeSummary || loading} />
        <PromptButton text="Next 3 Months" onClick={() => sendPredefinedPrompt('3 months')} disabled={!resumeSummary || loading} />
        <PromptButton text="Next 6 Months" onClick={() => sendPredefinedPrompt('6 months')} disabled={!resumeSummary || loading} />
      </div>

      <h2 style={{ marginTop: '0px' }}>Ask a Follow-up Question</h2>
      
      <div ref={chatContainerRef} style={{ maxWidth: '800px', height: '400px', overflowY: 'auto', padding: '10px', border: '1px solid #ccc', borderRadius: '6px', marginBottom: '20px', backgroundColor: 'white', whiteSpace: 'pre-wrap' }}>
        {messages.map((msg, idx) => (
          <div key={idx} style={{ marginBottom: '10px' }}>
            <strong>{msg.role === 'user' ? 'You' : 'GuideAI'}:</strong>
            <div style={{ display: 'inline' }}>
              <ReactMarkdown>{msg.content}</ReactMarkdown>
            </div>
          </div>
        ))}
      </div>

      <textarea rows="4" cols="60" placeholder="Ask a question..." value={input} onChange={(e) => setInput(e.target.value)} style={{ padding: '10px', fontSize: '16px', border: '1px solid #ccc', borderRadius: '4px', width: '100%', maxWidth: '800px' }} />
      <br />
      <button onClick={handleManualSend} disabled={loading} style={{ marginTop: '10px', padding: '10px 20px', fontSize: '16px', border: 'none', borderRadius: '4px', backgroundColor: '#007bff', color: 'white', cursor: loading ? 'not-allowed' : 'pointer' }}>
        {loading ? 'Thinking...' : 'Send'}
      </button>
    </div>
  );
}

export default Chatbot;